// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { RoleDetailsComponent } from './roles/role-details/role-details.component';
import { RoleEditComponent } from './roles/role-edit/role-edit.component';
import { RoleUsersFormComponent } from './roles/role-users-form/role-users-form.component';
import { RoleScopeFormComponent } from './roles/role-scope-form/role-scope-form.component';
import { RoleCreateNewComponent } from './roles/role-create-new/role-create-new.component';
import { RoleScopeFormGuardService } from './role-scope-form-guard.service';
import { ReviewNewPolicyComponent } from './review-new-policy/review-new-policy.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { ManageUserScopesComponent } from './roles/manage-user-scopes/manage-user-scopes.component';
import { GroupsListComponent } from './groups/groups-list/groups-list.component';
import { GroupCreateComponent } from './groups/group-create/group-create.component';
import { GroupDetailsComponent } from './groups/group-details/group-details.component';
import { GroupUsersFormComponent } from './groups/group-details/group-users-form/group-users-form.component';
import { GroupRolesFormComponent } from './groups/group-details/group-roles-form/group-roles-form.component';
import { GroupEditComponent } from './groups/group-edit/group-edit.component';
// tslint:disable-next-line:max-line-length
import { RoleDetailsGroupsAddComponent } from './roles/role-details/role-details-groups-add/role-details-groups-add.component';
// tslint:disable-next-line:max-line-length
import { RoleDetailsGroupAddScopeComponent } from './roles/role-details/role-details-group-add-scope/role-details-group-add-scope.component';
import { UsersComponent } from './users/users.component';
import { UsersListComponent } from './users/users-list/users-list.component';
import { UserDetailsComponent } from './users/user-details/user-details.component';
// tslint:disable-next-line:max-line-length
import { UserDetailsRolesTabAddComponent } from './users/user-details/user-details-roles-tab-add/user-details-roles-tab-add.component';
// tslint:disable-next-line:max-line-length
import { UserDetailsGroupsTabAddComponent } from './users/user-details/user-details-groups-tab-add/user-details-groups-tab-add.component';
import { PoliciesComponent } from './policies/policies.component';
import { PoliciesListComponent } from './policies/policies-list/policies-list.component';
import { PolicyCreateNewComponent } from './policies/policy-create-new/policy-create-new.component';
import { PolicyDetailsComponent } from './policies/policies-list/policy-details/policy-details.component';
import { PolicyEditComponent } from './policies/policy-edit/policy-edit.component';
// tslint:disable-next-line:max-line-length
import { PolicyStatementAddComponent } from './policies/policies-list/policy-details/policy-statements-list/policy-statement-add/policy-statement-add.component';
// tslint:disable-next-line:max-line-length
import { PolicyStatementEditComponent } from './policies/policies-list/policy-details/policy-statements-list/policy-statement-edit/policy-statement-edit.component';
import { RolesListComponent } from './roles/roles-list/roles-list.component';
import { RolePoliciesFormComponent } from './roles/role-policies-form/role-policies-form.component';
import { LoggedUserResolverService } from '@app/core/services/logged-user-resolver.service';
// tslint:disable-next-line:max-line-length
import { RoleDetailsGroupScopesComponent } from './roles/role-details/role-details-groups-tab/role-details-group-scopes/role-details-group-scopes.component';
// tslint:disable-next-line:max-line-length
import { UserDetailsRoleScopesComponent } from './users/user-details/user-details-roles-tab/user-details-role-scopes/user-details-role-scopes.component';
import { OrganizationsComponent } from './organizations/organizations.component';
import { OrganizationsListComponent } from './organizations/organizations-list/organizations-list.component';
import { OrganizationRolesListComponent } from './organizations/organization-roles-list/organization-roles-list.component';
import { OrganizationRoleUsersListComponent } from './organizations/organization-role-users-list/organization-role-users-list.component';
// tslint:disable-next-line:max-line-length
import { OrganizationRoleManageUserScopesComponent } from './organizations/organization-role-manage-user-scopes/organization-role-manage-user-scopes.component';

const routes: Routes = [
  {
    path: 'home',
    component: DashboardComponent
  },
  {
    path: 'roles',
    component: RolesListComponent
  },
  {
    path: 'roles/:id',
    component: RoleDetailsComponent
  },
  {
    path: 'roles/:id/add-users',
    component: RoleUsersFormComponent
  },
  {
    path: 'roles/:id/add-users/scope',
    component: RoleScopeFormComponent,
    canActivate: [RoleScopeFormGuardService]
  },
  {
    path: 'roles/:id/add-policies',
    component: RolePoliciesFormComponent
  },
  {
    path: 'roles/:id/add-groups',
    component: RoleDetailsGroupsAddComponent
  },
  {
    path: 'roles/:id/add-groups/scope',
    component: RoleDetailsGroupAddScopeComponent
  },
  {
    path: 'roles/:id/users/:username/scopes',
    component: ManageUserScopesComponent,
    canActivate: [RoleScopeFormGuardService]
  },
  {
    path: 'roles/:id/groups/:groupId',
    component: RoleDetailsGroupScopesComponent
  },
  {
    path: 'groups',
    component: GroupsListComponent
  },
  {
    path: 'groups/add-group',
    component: GroupCreateComponent,
    resolve: {
      currentUser: LoggedUserResolverService
    }
  },
  {
    path: 'groups/:id',
    component: GroupDetailsComponent
  },
  {
    path: 'groups/:id/edit',
    component: GroupEditComponent,
    resolve: {
      currentUser: LoggedUserResolverService
    }
  },
  {
    path: 'groups/:id/add-users',
    component: GroupUsersFormComponent
  },
  {
    path: 'groups/:id/add-roles',
    component: GroupRolesFormComponent
  },
  {
    path: 'policies',
    component: PoliciesComponent,
    children: [
      {
        path: '',
        component: PoliciesListComponent
      },
      {
        path: 'add-policy',
        component: PolicyCreateNewComponent
      },
      {
        path: 'add-policy/review',
        component: ReviewNewPolicyComponent
      },
      {
        path: ':id',
        component: PolicyDetailsComponent
      },
      {
        path: ':id/add-statement',
        component: PolicyStatementAddComponent
      },
      {
        path: ':id/statement/:statementId',
        component: PolicyStatementEditComponent
      },
      {
        path: ':id/edit',
        component: PolicyEditComponent
      }
    ]
  },
  {
    path: 'users',
    component: UsersComponent,
    children: [
      {
        path: '',
        component: UsersListComponent
      },
      {
        path: ':username',
        component: UserDetailsComponent
      },
      {
        path: ':username/add-roles',
        component: UserDetailsRolesTabAddComponent
      },
      {
        path: ':username/roles/:id',
        component: UserDetailsRoleScopesComponent
      },
      {
        path: ':username/add-groups',
        component: UserDetailsGroupsTabAddComponent
      }
    ]
  },
  {
    path: 'organizations',
    component: OrganizationsComponent,
    children: [
      {
        path: '',
        component: OrganizationsListComponent
      },
      {
        path: ':orgId',
        component: OrganizationRolesListComponent
      },
      {
        path: ':orgId/roles/:id',
        component: OrganizationRoleUsersListComponent
      },
      {
        path: ':orgId/roles/:id/users/:username/scopes',
        component: OrganizationRoleManageUserScopesComponent
      }
    ]
  },
  {
    path: '**',
    redirectTo: 'home'
  }
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule]
})
export class AccessManagementRoutingModule {}



// WEBPACK FOOTER //
// ./src/app/access-management/access-management-routing.module.ts